import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import DateTime from 'react-datetime'
import {
  Button,
  Input,
  LoadingFull,
  Select,
  SelectAsync,
  Checkbox,
  selectComponents,
  DataTable,
} from '@arnsbogroup/react-voicearchive'

import debounce from 'debounce-promise'
import { createPortal } from 'react-dom'
import { useHistory, Link } from 'react-router-dom'
import classNames from 'classnames'
import useCurrentUser from 'hooks/useCurrentUser'
import useApi from 'hooks/useApi'

const SearchPage = ({
  baseApiUrl,
  components = {},
  body = {},
  head = {},
  indicators = [],
  messages: resultMessages = {},
  containerClassName = "",
  rowSelectionKey = "uid",
  onRowSelected = null,
  rowsSelected = [],
  ...props
}) => {
  const [state, setState] = useState({})
  const [options, setOptions] = useState({})
  const [sorting, setSorting] = useState({
    key: 'uid',
    direction: 'DESC',
  })
  const [schema] = useApi(`${baseApiUrl}/form-schema`)
  const [
    results,
    loading,
    errors,
    refetch,
    meta,
    pagination
  ] = useApi(baseApiUrl, {
    load: false,
    pagination: { pageSize: 20 },
    includeRelations: true,
    sorting: {
      key: sorting.key,
      direction: sorting.direction,
    },
  })

  const [show, setShow] = useState(props.match.isExact)
  const [checked, setChecked] = useState(false)
  const history = useHistory()

  const onRefetch = useCallback(() => {
    refetch(baseApiUrl, { sorting, params: state })
  }, [state, sorting])

  useEffect(() => {
    if (schema?.length) {
      const nextState = schema
        .reduce((memo, { searchOptionName, defaultValue, valueType }) =>
          ({
            ...memo,
            [searchOptionName]: getDefaultValue(searchOptionName, defaultValue)
          })
          , {})
      setState(nextState)
    }
  }, [schema, props?.location?.search])

  const getDefaultValue = (searchOptionName, defaultValue) => {
    let passedSearchParams = {}
    const params = new URLSearchParams(props?.location?.search)
    const searchParam = params.entries()
    for (const [key,value] of searchParam) {
      passedSearchParams[key] = [value]
    }
    if ( searchOptionName in passedSearchParams ) {
      const options = schema.find(obj => obj.searchOptionName == searchOptionName).options;
      if (!options) {
        return passedSearchParams[searchOptionName]
      }
      return options
        .filter(({ uid, name }) => uid == passedSearchParams[searchOptionName] || name == passedSearchParams[searchOptionName])
        .map(({ uid, name }) => ({ value: uid, label: name }))

    }
    if (!defaultValue) {
      return ''
    } else if (Array.isArray(defaultValue)) {
      return defaultValue.map(({ uid, name }) => ({ value: uid, label: name }))
    } else {
      return defaultValue
    }
  }

  useKeyboardShortcut(['Enter'], onRefetch)
  useEffect(onRefetch, [sorting, state])

  useEffect(() => {
    if (!props.match.isExact && !/^\d/.test(props.location.pathname.replace(props.match.url + '/', ''))) {
      setShow(false)
    } else if (props.match.isExact && !show) {
      setShow(true)
    }
  }, [props?.location.pathname])

  if (!show) {
    return null
  }

  return (
    <div className={classNames('flex flex-col h-full', !props.match.isExact ? 'w-1/4 border-r border-gray-100' : 'w-full', containerClassName)}>
      <div className={classNames('shrink-0 p-2 shadow justify-between space-y-2', { 'flex': props.match.isExact })}>
        <div className={classNames('grow grid gap-2', { 'grid-cols-4': props.match.isExact })}>
          { !props.match.isExact &&
            <div className="flex justify-end text-sm">
              <Button
                variant="inline"
                onClick={() => setShow(false)}
                icon={['fas', 'times']}
                iconPosition="right"
              >
                Close search results
              </Button>
            </div>
          }

          { !schema ? [...Array(4)].map((_, i) => (
            <div key={i} className="pulse w-full h-full bg-gray-100 rounded-xs" />
          )) : schema.filter(({ hidden }) => !hidden).slice(0, props.match.isExact ? undefined : 3).map((param, i) => (
            <div className="my-auto" key={param.searchOptionName}>
              { param.fieldType == 'input' ? (
                <Input
                  key={`input-${state[param.searchOptionName]}`}
                  defaultValue={state[param.searchOptionName]}
                  onBlur={({ target: { value } }) => {
                    if (state[param.searchOptionName] != value) {
                      setState((prev) => ({ ...prev, [param.searchOptionName]: value }))
                    }
                  }}
                  placeholder={param.title}
                  className="border-gray-200"
                  autoFocus={i == 0}
                  onKeyDown={({ key, target: { value } }) => {
                    if (key == 'Enter') {
                      setState((prev) => ({ ...prev, [param.searchOptionName]: value }))
                    }
                  }}
                />
              ) : param.fieldType == 'checkbox' ? (
                <div className='flex justify-center'>
                  <Checkbox
                    key={`checkbox-${state[param.searchOptionName]}`}
                    defaultValue={state[param.searchOptionName]}
                    onChange={() => {
                      setState((prev) => ({ ...prev, [param.searchOptionName]: !checked }))
                      setChecked(!checked)
                    }}
                    checked={checked}
                    label={param.title}
                    autoFocus={i == 0}
                    labelClassNames="text-gray-400"
                  />
                </div>
              ) : param.fieldType == 'dateTime' ? (
                <DateTime
                  onChange={(value) => setState((prev) => ({ ...prev, [param.searchOptionName]: Math.round(new Date(value).getTime() / 1000) }))}
                  closeOnSelect
                  className="w-full p-4 transition duration-200 border peer border-gray-300 focus:border-primary"
                  inputProps={{
                    placeholder: param.title,
                    className: "border-none"
                  }}
                />
              ) : Array.isArray(param.options) ? (
                <Select
                  key={`select-${state[param.searchOptionName]}`}
                  value={state[param.searchOptionName]}
                  onChange={(value) => setState((prev) => ({ ...prev, [param.searchOptionName]: value }))}
                  options={param.options.map(({ uid, name }) => ({ value: uid, label: name }))}
                  placeholder={param.title}
                  floatingPlaceholder={param.title}
                  isClearable
                  openMenuOnFocus
                  isMulti={param.multiple}
                  autoFocus={i == 0}
                />
              ) : (
                <SelectAsync
                  key={`select-async-${state[param.searchOptionName]}`}
                  value={state[param.searchOptionName]}
                  onChange={(value) => setState((prev) => ({ ...prev, [param.searchOptionName]: value }))}
                  cacheOptions
                  loadOptions={debounce((query) => {
                    return new Promise((resolve, reject) => {
                      fetch(`${param.options}?search[name]=${query}`)
                        .then(response => response.json())
                        .then((result) => (
                          resolve((Array.isArray(result) ? result : result?.data)
                            .map(({ uid, name }) => ({ value: uid, label: name })))
                        ))
                        .catch(reject)
                    })
                  }, 250)}
                  placeholder={param.title}
                  floatingPlaceholder={param.title}
                  isClearable
                  openMenuOnFocus
                  defaultOptions
                  isMulti={param.multiple}
                  autoFocus={i == 0}
                />
              ) }
            </div>
          )) }
        </div>

        <div className={classNames('shrink-0 flex items-center justify-between space-x-2', { 'p-2': props.match.isExact, 'text-sm': !props.match.isExact })}>
          { !props.match.isExact &&
            <Link to={props.match.url}>
              <Button
                variant="inline"
                icon={['fas', 'list']}
                size={props.match.isExact ? 'base' : 'sm'}
              >
                <FormattedMessage {...messages.back } />
              </Button>
            </Link>
          }

          <div className="space-x-4">
            <Button
              variant="inline"
              onClick={() => setState({})}
              icon={['fas', 'redo']}
              size={props.match.isExact ? 'base' : 'sm'}
            >
              <FormattedMessage {...messages.reset } />
            </Button>

            <Button onClick={onRefetch} size={props.match.isExact ? 'base' : 'sm'}>
              <FormattedMessage {...messages.search} />
            </Button>
          </div>
        </div>
      </div>

      <div className="grow overflow-y-scroll">
        { loading ? (
          <LoadingFull />
        ) : (
          <DataTable
            components={components}
            head={head}
            body={body}
            entries={results}
            indicators={indicators}
            messages={{}}
            setSorting={setSorting}
            sorting={sorting}
            state={state}
            rowSelectionKey={rowSelectionKey}
            onRowSelected={onRowSelected}
            rowsSelected={rowsSelected}
          />
        ) }
      </div>

      <div className="shrink-0 flex items-center justify-between border-t border-gray-200">
        <span className="p-2 text-sm text-gray-600 inline-flex justify-between">
          { pagination?.totalPages ? (
            <FormattedMessage
              {...messages.countOf}
              values={{
                ...pagination,
                from: parseInt(pagination.pageSize) * parseInt(pagination.page),
                to: parseInt(pagination.pageSize) * (parseInt(pagination.page) + 1),
              }}
            />
          ) : (
            <FormattedMessage
              {...messages.count}
              values={{ count: results?.length || 0 }}
            />
          ) }
        </span>

        <div className="space-x-2 px-4">
          <Button
            onClick={pagination.prev}
            variant="inline"
            size="sm"
          >
            <FormattedMessage {...messages.prev } />
          </Button>

          <Button
            onClick={pagination.next}
            variant="inline"
            size="sm"
          >
            <FormattedMessage {...messages.next } />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SearchPage
